import axios from 'axios';
import { debounce } from 'lodash';
import { getNonGwApiRoot, getApiRoot } from '../../constants/ParamountReactConstants';
import { createURL, generateUUID } from '../../utils/Utils';
import { ImportTypes } from '../../../types/import_export';
import {
  createNewUpload,
  updateUploadProgress,
  uploadFailed,
  uploadFinished,
} from '../parent/language/update';

const CancelToken = axios.CancelToken;

// Cancel tokens for search operations to prevent race conditions
let searchReceiverSkuCancelToken: any;
let fetchReceiverSkusCancelToken: any;

export function fetchReceiverAdoptionStats(receiverId: number) {
  return {
    type: 'FETCH_RECEIVER_ADOPTION_STATS',
    payload: axios.get(`${getNonGwApiRoot()}/distribution/receivers/${receiverId}/adoption-stats`),
    meta: { receiverId },
  };
}

export function refreshReceiverAdoptionStats(receiverId: number) {
  return {
    type: 'REFRESH_RECEIVER_ADOPTION_STATS',
    payload: axios.post(
      `${getNonGwApiRoot()}/distribution/receivers/${receiverId}/adoption-stats/refresh`,
      { forceRecalculate: true, notifyOnCompletion: true }
    ),
    meta: { receiverId },
  };
}

export function checkJobStatus(jobId: string) {
  return {
    type: 'CHECK_ADOPTION_JOB_STATUS',
    payload: axios.get(`${getNonGwApiRoot()}/jobs/${jobId}/status`),
    meta: { jobId },
  };
}

export function searchReceiverSku(receiverId: number, sku: string) {
  // Cancel previous search request to prevent race conditions
  if (searchReceiverSkuCancelToken) searchReceiverSkuCancelToken();

  return {
    type: 'SEARCH_RECEIVER_SKU',
    payload: axios.get(
      `${getNonGwApiRoot()}/distribution/receivers/${receiverId}/sku-search${createURL([
        { name: 'sku', values: sku },
      ])}`,
      {
        cancelToken: new CancelToken(c => {
          searchReceiverSkuCancelToken = c;
        }),
      }
    ),
    meta: { receiverId, sku },
  };
}

// New paginated SKU endpoint
export function fetchReceiverSkus(
  receiverId: number,
  params: {
    page?: number;
    limit?: number;
    sku?: string;
    search?: string;
    sort?: string;
    order?: string;
  } = {}
) {
  // Cancel previous fetch request to prevent race conditions
  if (fetchReceiverSkusCancelToken) fetchReceiverSkusCancelToken();

  const urlParams = [];
  if (params.page) urlParams.push({ name: 'page', values: params.page });
  if (params.limit) urlParams.push({ name: 'limit', values: params.limit });
  if (params.sku) urlParams.push({ name: 'sku', values: params.sku });
  if (params.search) urlParams.push({ name: 'search', values: params.search });
  if (params.sort) urlParams.push({ name: 'sort', values: params.sort });
  if (params.order) urlParams.push({ name: 'order', values: params.order });

  return {
    type: 'FETCH_RECEIVER_SKUS',
    payload: axios.get(
      `${getNonGwApiRoot()}/distribution/receivers/${receiverId}/skus${createURL(urlParams)}`,
      {
        cancelToken: new CancelToken(c => {
          fetchReceiverSkusCancelToken = c;
        }),
      }
    ),
    meta: { receiverId, params },
  };
}

export function fetchPresignedUrlForAdoption(filename: string) {
  const params = createURL([{ name: 'filename', values: filename }]);
  return {
    type: 'FETCH_ADOPTION_PRESIGNED_URL',
    payload: axios.get(`${getApiRoot()}/imports/presigned_url${params}`),
  };
}

function createAdoptionExcelImport(file: File) {
  return async (dispatch: any) => {
    const uuid = generateUUID();
    dispatch(createNewUpload(uuid, file.name, ImportTypes.EXCEL));

    const urlResponse: any = await dispatch(fetchPresignedUrlForAdoption(file.name));
    const bucket = urlResponse.value.data;
    const url = bucket.url;
    const filename = bucket.filename;

    await axios.put(url, file, {
      headers: { 'Content-Type': file.type },
      // Send upload progress for file upload.
      onUploadProgress: debounce(progress => {
        dispatch(updateUploadProgress(uuid, progress));
      }, 100),
    });

    try {
      const response = await axios.post(
        `${getApiRoot()}/imports/adoption_files?file_name=${encodeURIComponent(filename)}`
      );
      const logId = response.data.import_log_id || response.data.id;
      dispatch(uploadFinished(uuid, logId));
      return response;
    } catch (e) {
      dispatch(uploadFailed(uuid));
      throw e;
    }
  };
}

export function importAdoptionFile(file: File) {
  return (dispatch: any) => {
    return dispatch(createAdoptionExcelImport(file));
  };
}

export function fetchAdoptionImportLogs(
  params: {
    page?: number;
    limit?: number;
  } = {}
) {
  const urlParams = createURL([
    { name: 'file_type_ids', values: 26 },
    { name: 'page', values: params.page || 1 },
    { name: 'limit', values: params.limit || 50 },
  ]);

  return {
    type: 'FETCH_ADOPTION_IMPORT_LOGS',
    payload: axios.get(`${getNonGwApiRoot()}/imports/logs${urlParams}`),
    meta: { page: params.page || 1 },
  };
}
